import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Sparkles, Plus, Search, Filter, Calendar, X,
  AlertTriangle, Check, Trash2, Edit3, Lock, UploadCloud
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { buildApiUrl } from '../../utils/apiSetup';
import BulkLICModal from './BulkLICModal';

const LICManagement = () => {
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const canView = hasPermission('lic:view');
  const canAdd = hasPermission('lic:add');
  const canStop = hasPermission('lic:stop');
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals state
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecordForStop, setSelectedRecordForStop] = useState(null);
  const [isSubmittingRecord, setIsSubmittingRecord] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Helper functions for policy entries
  const getCurrentMonthIso = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const createEmptyPolicyEntry = (previous) => ({
    tempId: Date.now() + Math.random(),
    lic_number: '',
    monthly_premium: '',
    effective_from_month: previous ? previous.effective_from_month : getCurrentMonthIso()
  });

  // Form states
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [policyEntries, setPolicyEntries] = useState([createEmptyPolicyEntry()]);

  const [stopForm, setStopForm] = useState({
    stop_month: new Date().getMonth() + 1,
    stop_year: new Date().getFullYear()
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch LIC Records
      const recordsRes = await fetch(buildApiUrl('/api/v1/lic'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      const recordsData = await recordsRes.json();
      setRecords(Array.isArray(recordsData) ? recordsData : []);

      // Fetch Employees for dropdown
      const empRes = await fetch(buildApiUrl('/api/v1/benfish-employees/search?limit=1000'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      const empData = await empRes.json();
      const mappedEmployees = (empData.employees || [])
        .filter(emp => emp.emp_code !== 'ADMIN001') // Exclude System Administrator
        .map(emp => ({
          ...emp,
          full_name: emp.full_name || emp.emp_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
        }));
      setEmployees(mappedEmployees);

    } catch (err) {
      console.error('Failed to load LIC record data:', err);
      showError('Failed to load LIC record data');
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 2 + i);

  // Policy Entry Handlers
  const handleAddPolicyEntry = () => {
    setPolicyEntries(prev => {
      const last = prev[prev.length - 1];
      return [...prev, createEmptyPolicyEntry(last)];
    });
  };

  const handleRemovePolicyEntry = (index) => {
    if (policyEntries.length <= 1) return;
    setPolicyEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handlePolicyEntryChange = (index, field, value) => {
    setPolicyEntries(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // Form submit for Add (Single or Multiple) / Edit
  const handleRecordSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEmployeeId) {
      showError('Please select an employee.');
      return;
    }

    if (!policyEntries || policyEntries.length === 0) {
      showError('Please add at least one policy entry.');
      return;
    }

    // In-batch validation
    const seenBatchNumbers = new Set();
    for (let i = 0; i < policyEntries.length; i++) {
      const entry = policyEntries[i];
      const policyLabel = policyEntries.length > 1 ? `Policy #${i + 1}: ` : '';

      const cleanNum = (entry.lic_number || '').trim();
      if (!cleanNum) {
        showError(`${policyLabel}LIC Policy Number is required.`);
        return;
      }

      const normalizedNum = cleanNum.toUpperCase();
      if (seenBatchNumbers.has(normalizedNum)) {
        showError(`Duplicate policy number '${cleanNum}' detected in the submitted batch.`);
        return;
      }
      seenBatchNumbers.add(normalizedNum);

      const premium = parseFloat(entry.monthly_premium);
      if (isNaN(premium) || premium <= 0) {
        showError(`${policyLabel}Monthly Premium must be a positive number greater than 0.`);
        return;
      }

      if (!entry.effective_from_month) {
        showError(`${policyLabel}Effective Recovery Month is required.`);
        return;
      }
    }

    setIsSubmittingRecord(true);
    try {
      if (editingRecord) {
        const entry = policyEntries[0];
        let effectiveDate = entry.effective_from_month;
        if (effectiveDate && effectiveDate.length === 7) {
          effectiveDate = `${effectiveDate}-01`;
        }

        const bodyData = {
          lic_number: entry.lic_number.trim(),
          monthly_premium: parseFloat(entry.monthly_premium),
          effective_from_month: effectiveDate
        };

        const res = await fetch(buildApiUrl(`/api/v1/lic/${editingRecord.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(bodyData)
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Failed to update LIC record');
        }

        showSuccess('LIC record successfully updated!');
      } else {
        const payload = {
          employee_id: parseInt(selectedEmployeeId),
          policies: policyEntries.map(entry => {
            let effDate = entry.effective_from_month;
            if (effDate && effDate.length === 7) {
              effDate = `${effDate}-01`;
            }
            return {
              lic_number: entry.lic_number.trim(),
              monthly_premium: parseFloat(entry.monthly_premium),
              effective_from_month: effDate
            };
          })
        };

        const res = await fetch(buildApiUrl('/api/v1/lic/multiple'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Failed to save LIC policy(s)');
        }

        const count = policyEntries.length;
        showSuccess(
          count > 1
            ? `Successfully created ${count} LIC policies for employee!`
            : 'LIC policy successfully created!'
        );
      }

      setShowRecordModal(false);
      setEditingRecord(null);
      setSelectedEmployeeId('');
      setPolicyEntries([createEmptyPolicyEntry()]);
      loadData();
    } catch (err) {
      showError(err.message);
    } finally {
      setIsSubmittingRecord(false);
    }
  };

  // Form submit for Stop Recovery
  const handleStopSubmit = async (e) => {
    e.preventDefault();
    try {
      const stopMonthStr = String(stopForm.stop_month).padStart(2, '0');
      const stopDate = `${stopForm.stop_year}-${stopMonthStr}-01`;

      const res = await fetch(buildApiUrl(`/api/v1/lic/${selectedRecordForStop.id}/stop-recovery?stop_month=${stopDate}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to stop recovery');
      }

      showSuccess('LIC policy recovery successfully stopped.');
      setShowStopModal(false);
      setSelectedRecordForStop(null);
      loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setSelectedEmployeeId(String(record.employee_id));
    const dateStr = record.effective_from_month
      ? record.effective_from_month.substring(0, 7)
      : getCurrentMonthIso();
    setPolicyEntries([
      {
        tempId: record.id,
        lic_number: record.lic_number,
        monthly_premium: record.monthly_premium,
        effective_from_month: dateStr
      }
    ]);
    setShowRecordModal(true);
  };

  const handleOpenAddModal = () => {
    setEditingRecord(null);
    setSelectedEmployeeId('');
    setPolicyEntries([createEmptyPolicyEntry()]);
    setShowRecordModal(true);
  };

  const handleStopRecoveryClick = (record) => {
    setSelectedRecordForStop(record);
    setShowStopModal(true);
  };

  // Filter implementation
  const filteredRecords = records.filter(record => {
    const empName = record.employee_name || '';
    const empCode = record.employee_code || '';
    const policyNo = record.lic_number || '';
    const matchesSearch =
      empName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      empCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policyNo.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (!canView) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-xl p-8 text-center text-gray-500 border border-gray-100">
          You do not have permission to view employee LIC records.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-blue-500 rounded-xl text-white">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">LIC Policy Management</h1>
              <p className="text-sm text-gray-500">Configure employee LIC policies and monthly salary recovery</p>
            </div>
          </div>
        </div>
        {canAdd && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition shadow-sm font-semibold text-sm"
            >
              <UploadCloud className="w-4 h-4 mr-2" /> Bulk Upload Excel
            </button>
            <button
              onClick={handleOpenAddModal}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm font-semibold text-sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add LIC Policy
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="bg-white p-4 rounded-xl border border-gray-100 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search by employee name, code or policy number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-200 rounded-lg py-1.5 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="stopped">Stopped</option>
              </select>
            </div>
          </div>

          {/* LIC Records Grid */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Policy Number</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Monthly Premium</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Effective From</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Stopped From</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-sm">
                  {filteredRecords.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-10 text-center text-gray-500 font-medium">
                        No LIC records found matching the criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{record.employee_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{record.employee_code || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-700">
                          {record.lic_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-950">
                          ₹{parseFloat(record.monthly_premium).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {new Date(record.effective_from_month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            record.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {record.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {record.stopped_from_month
                            ? new Date(record.stopped_from_month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center space-x-2">
                          {canAdd && (
                            <button
                              onClick={() => handleEdit(record)}
                              className="inline-flex items-center p-1.5 border border-gray-200 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition"
                              title="Edit Policy"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                          )}
                          {record.status === 'active' ? (
                            canStop && (
                              <button
                                onClick={() => handleStopRecoveryClick(record)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-red-200 rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition text-xs font-bold"
                              >
                                Stop Recovery
                              </button>
                            )
                          ) : (
                            <button
                              disabled
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-200 rounded-md text-gray-400 bg-gray-50 text-xs font-bold cursor-not-allowed"
                            >
                              <Lock className="w-3.5 h-3.5 mr-1" /> Stopped
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Record Creation & Update Modal */}
      {showRecordModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-gray-100 overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-gray-50/70 shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {editingRecord ? 'Edit LIC Policy' : 'Add Employee LIC Policy(s)'}
                </h3>
                {!editingRecord && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Select an employee and configure one or more LIC policies in this form.
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowRecordModal(false)}
                className="p-1 hover:bg-gray-200 rounded-full transition"
                title="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleRecordSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto space-y-5 flex-1">
                {/* Employee Selection */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Employee *
                  </label>
                  <select
                    required
                    disabled={!!editingRecord}
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-2.5 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        [{emp.emp_code}] {emp.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Policies Section Header */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      {editingRecord ? 'Policy Details' : `LIC Policies to Add (${policyEntries.length})`}
                    </h4>
                    {!editingRecord && (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Add one or more LIC policies for the selected employee
                      </p>
                    )}
                  </div>
                  {!editingRecord && (
                    <button
                      type="button"
                      onClick={handleAddPolicyEntry}
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-bold transition border border-blue-200 shadow-xs"
                      title="Add another policy for this employee"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add Policy</span>
                    </button>
                  )}
                </div>

                {/* Policies List */}
                <div className="space-y-4">
                  {policyEntries.map((entry, index) => (
                    <div
                      key={entry.tempId || index}
                      className="p-4 bg-gray-50/70 border border-gray-200 rounded-xl space-y-3 relative transition hover:border-gray-300"
                    >
                      {/* Policy header with numbering & remove button */}
                      {!editingRecord && (
                        <div className="flex items-center justify-between pb-2 border-b border-gray-200/60">
                          <span className="px-2.5 py-0.5 text-xs font-bold rounded-md bg-blue-100 text-blue-800">
                            Policy #{index + 1}
                          </span>
                          {policyEntries.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemovePolicyEntry(index)}
                              className="inline-flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded transition font-medium"
                              title="Remove this policy"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Remove</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* Policy Number */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                          LIC Policy Number *
                        </label>
                        <input
                          type="text"
                          required
                          value={entry.lic_number}
                          onChange={(e) => handlePolicyEntryChange(index, 'lic_number', e.target.value)}
                          placeholder="e.g. LIC123456"
                          className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                      </div>

                      {/* Monthly Premium and Effective Month */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                            Monthly Premium (₹) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            required
                            value={entry.monthly_premium}
                            onChange={(e) => handlePolicyEntryChange(index, 'monthly_premium', e.target.value)}
                            placeholder="e.g. 1500.00"
                            className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                            Effective Recovery Month *
                          </label>
                          <input
                            type="month"
                            required
                            value={entry.effective_from_month}
                            onChange={(e) => handlePolicyEntryChange(index, 'effective_from_month', e.target.value)}
                            className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end items-center space-x-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowRecordModal(false)}
                  disabled={isSubmittingRecord}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingRecord}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-sm transition disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  {isSubmittingRecord ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>
                      {editingRecord
                        ? 'Save Changes'
                        : policyEntries.length > 1
                        ? `Save ${policyEntries.length} Policies`
                        : 'Save Policy'}
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stop Recovery Modal */}
      {showStopModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-gray-100 relative">
            <button
              onClick={() => setShowStopModal(false)}
              className="absolute right-4 top-4 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" /> Stop Policy Recovery
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Specify the month starting from which recovery should be stopped for policy <strong>{selectedRecordForStop?.lic_number}</strong>.
            </p>
            <form onSubmit={handleStopSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Month</label>
                  <select
                    value={stopForm.stop_month}
                    onChange={(e) => setStopForm({ ...stopForm, stop_month: parseInt(e.target.value) })}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Year</label>
                  <select
                    value={stopForm.stop_year}
                    onChange={(e) => setStopForm({ ...stopForm, stop_year: parseInt(e.target.value) })}
                    className="w-full border border-gray-200 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                <button
                  type="button"
                  onClick={() => setShowStopModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-bold text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shadow-sm"
                >
                  Confirm Stop
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Bulk Upload Modal */}
      <BulkLICModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          loadData();
          showSuccess('LIC policies bulk upload processed successfully!');
        }}
      />
    </div>
  );
};

export default LICManagement;
